import { useState, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { FormValues } from './useMainSubForm';
import { calcTotal } from 'src/slices/proposalsSlice';
import type { ProposalItemEntity } from 'src/db/entities/proposal/ProposalEntity';
import { DISCOUNT_TYPES } from 'src/db/entities/proposal/ProposalEntity';

interface PriceResult {
  subtotal: number;
  total: number;
}

/**
 * Hook to calculate price (subtotal & total) based on form values.
 * Uses useWatch to subscribe to nested changes in items.
 */
const usePriceCalculation = ({ taxRate }: { taxRate: number }): PriceResult => {
  const { control } = useFormContext<FormValues>();

  // Subscribe to form fields; useWatch tracks nested changes in 'items'
  const items = useWatch({ control, name: 'items' }) as ProposalItemEntity[];
  const discountType = useWatch({ control, name: 'discountType' }) as DISCOUNT_TYPES;
  const discountAmount = (useWatch({ control, name: 'discountAmount' }) as number) || 0;
  const includesTax = useWatch({ control, name: 'includesTax' }) as boolean;

  // Local state for the calculated price
  const [price, setPrice] = useState<PriceResult>({ subtotal: 0, total: 0 });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await calcTotal({
          items,
          includesTax,
          taxRate,
          discountType,
          discountAmount,
        });

        if (!cancelled) {
          setPrice({
            subtotal: result.subtotal,
            total: result.total,
          });
        }
      } catch (err) {
        console.error('Price calculation failed:', err);
        // Optionally set error state here
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [items, discountType, discountAmount, includesTax, taxRate]);

  return price;
};

export default usePriceCalculation;
